import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { Group } from '@/common/types/group';
import InitialsCircle from '@/common/ui/InitialsCircle';
import TableInitialsCircle from '@/common/ui/TableInitialsCircle';
import { DotsThreeVerticalIcon, TrashIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import { TableCell, TableRow } from '@shadcn/components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface GroupTableRowProps {
  group: Group;
  onConfirmDeleteGroup: () => void;
}

export default function GroupTableRow({
  group,
  onConfirmDeleteGroup,
}: GroupTableRowProps) {
  const navigate = useNavigate();

  const { students } = useStudents();
  const { organisation } = useOrganisation();

  const groupStudents = students.filter((student) =>
    group.studentIds.includes(student.id),
  );
  const groupManagers = (organisation?.members ?? []).filter((member) =>
    group.managerIds.includes(member.uid),
  );

  const [isDropdownOpen, setDropdownOpen] = useState(false);

  function handleNavigateToGroup() {
    navigate(`/dashboard/groups/${group.id}`);
  }

  return (
    <TableRow className="cursor-pointer" onClick={handleNavigateToGroup}>
      <TableCell className="w-4"></TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          <TableInitialsCircle userName={`${group.name}`} size={24} />
          {group.name}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center pl-1">
          {groupStudents.slice(0, 10).map((student) => (
            <Tooltip key={student.id}>
              <TooltipTrigger
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/dashboard/learners/${student.id}`);
                }}
              >
                <div className="ml-[-6px]">
                  <InitialsCircle
                    userName={`${student.studentFirstName} ${student.studentSurname}`}
                    size={20}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {student.studentFirstName} {student.studentSurname}
              </TooltipContent>
            </Tooltip>
          ))}
          {groupStudents.length > 10 && (
            <Tooltip>
              <TooltipTrigger>
                <div className="ml-[-6px]">
                  <InitialsCircle
                    userName={`+${groupStudents.length - 10}`}
                    size={20}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {groupStudents
                  .slice(2)
                  .map(
                    (student) =>
                      `${student.studentFirstName} ${student.studentSurname}`,
                  )
                  .join(', ')}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center pl-1">
          {groupManagers.slice(0, 10).map((manager) => (
            <Tooltip key={manager.uid}>
              <TooltipTrigger>
                <div className="ml-[-6px]">
                  <InitialsCircle userName={manager.name} size={20} />
                </div>
              </TooltipTrigger>
              <TooltipContent>{manager.name}</TooltipContent>
            </Tooltip>
          ))}
          {groupManagers.length > 10 && (
            <Tooltip>
              <TooltipTrigger>
                <div className="ml-[-6px]">
                  <InitialsCircle
                    userName={`+${groupManagers.length - 10}`}
                    size={20}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {groupManagers
                  .slice(2)
                  .map((manager) => manager.name)
                  .join(', ')}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </TableCell>
      <TableCell className="flex justify-end mr-8">
        <DropdownMenu open={isDropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="p-1/2 mt-1"
              onClick={(e) => e.stopPropagation()}
            >
              <DotsThreeVerticalIcon size={24} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
            <DropdownMenuLabel>{group.name}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="flex gap-2"
              onClick={() => {
                setDropdownOpen(false);
                onConfirmDeleteGroup();
              }}
            >
              <TrashIcon weight="bold" size={16} color="red" /> Delete group
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
